import type {
  Account,
  AccountKey,
  BankOption,
  CreateAccountBody,
  UpdateAccountBody,
} from "@/services/dtos/store-bank-account.dto";

export const ACCOUNT_FORM_MODE = {
  CREATE: "create",
  EDIT: "edit",
} as const;

export type AccountFormMode =
  (typeof ACCOUNT_FORM_MODE)[keyof typeof ACCOUNT_FORM_MODE];

export interface BankSelectOption {
  value: number;
  label: string;
  bankNumber: number;
}

export interface AccountFormValues {
  bank: BankSelectOption | null;
  agency: string;
  account: string;
  active: boolean;
}

export interface ValidatedAccountFormValues {
  bank: BankSelectOption;
  agency: string;
  account: string;
  active: boolean;
}

export interface AccountFormErrors {
  bank?: string;
  agency?: string;
  account?: string;
}

export const EMPTY_ACCOUNT_FORM_VALUES: AccountFormValues = {
  bank: null,
  agency: "",
  account: "",
  active: true,
};

export const ACCOUNT_FORM_FIELD_LIMITS = {
  AGENCY_MAX_LENGTH: 6,
  ACCOUNT_MAX_LENGTH: 15,
} as const;

export const ACCOUNT_FORM_LABELS = {
  BANK: "Banco",
  AGENCY: "Agência",
  ACCOUNT: "Conta",
  ACTIVE: "Ativo",
} as const;

export const ACCOUNT_FORM_PLACEHOLDERS = {
  BANK: "Selecione o banco",
} as const;

export const ACCOUNT_FORM_FIELD_IDS = {
  BANK_LABEL: "account-form-bank-label",
  AGENCY: "account-form-agency",
  ACCOUNT: "account-form-account",
  ACTIVE: "account-form-active",
} as const;

export const ACCOUNT_FORM_TITLES: Record<AccountFormMode, string> = {
  [ACCOUNT_FORM_MODE.CREATE]: "Incluir conta corrente",
  [ACCOUNT_FORM_MODE.EDIT]: "Editar conta corrente",
};

export const ACCOUNT_FORM_DESCRIPTIONS: Record<AccountFormMode, string> = {
  [ACCOUNT_FORM_MODE.CREATE]:
    "Cadastre uma nova conta corrente para a loja selecionada.",
  [ACCOUNT_FORM_MODE.EDIT]: "Altere os dados da conta corrente selecionada.",
};

export const ACCOUNT_FORM_SUBMIT_LABELS: Record<AccountFormMode, string> = {
  [ACCOUNT_FORM_MODE.CREATE]: "Incluir",
  [ACCOUNT_FORM_MODE.EDIT]: "Salvar",
};

export const ACCOUNT_FORM_SUBMITTING_LABELS: Record<AccountFormMode, string> =
  {
    [ACCOUNT_FORM_MODE.CREATE]: "Incluindo...",
    [ACCOUNT_FORM_MODE.EDIT]: "Salvando...",
  };

export const ACCOUNT_FORM_SUCCESS_MESSAGES: Record<AccountFormMode, string> = {
  [ACCOUNT_FORM_MODE.CREATE]: "Conta corrente incluída com sucesso.",
  [ACCOUNT_FORM_MODE.EDIT]: "Conta corrente atualizada com sucesso.",
};

export const ACCOUNT_FORM_ERROR_MESSAGES = {
  BANK_REQUIRED: "Selecione um banco.",
  AGENCY_REQUIRED: "Informe a agência.",
  ACCOUNT_REQUIRED: "Informe a conta.",
  GENERIC_SAVE_ERROR:
    "Não foi possível salvar a conta corrente. Tente novamente.",
  MISSING_ACCOUNT_FOR_EDIT: "Conta corrente não informada para edição.",
  MISSING_STORE_FOR_CREATE: "Loja não informada para inclusão.",
} as const;

export const CANCEL_BUTTON_LABEL = "Cancelar";

export function buildBankOptionLabel(
  bankNumber: number,
  name: string,
): string {
  return `${bankNumber} - ${name}`;
}

export function toBankSelectOptions(banks: BankOption[]): BankSelectOption[] {
  return banks.map((bank) => ({
    value: bank.internalCode,
    label: buildBankOptionLabel(bank.number, bank.name),
    bankNumber: bank.number,
  }));
}

export function buildInitialFormValues(
  mode: AccountFormMode,
  account: Account | null,
): AccountFormValues {
  if (mode === ACCOUNT_FORM_MODE.EDIT && account) {
    return {
      bank: {
        value: account.internal_code,
        label: buildBankOptionLabel(account.bank_number, account.bank_name),
        bankNumber: account.bank_number,
      },
      agency: account.agency,
      account: account.account,
      active: account.active,
    };
  }
  return EMPTY_ACCOUNT_FORM_VALUES;
}

export function validateAccountFormValues(
  values: AccountFormValues,
): AccountFormErrors {
  const errors: AccountFormErrors = {};
  if (!values.bank) errors.bank = ACCOUNT_FORM_ERROR_MESSAGES.BANK_REQUIRED;
  if (!values.agency.trim())
    errors.agency = ACCOUNT_FORM_ERROR_MESSAGES.AGENCY_REQUIRED;
  if (!values.account.trim())
    errors.account = ACCOUNT_FORM_ERROR_MESSAGES.ACCOUNT_REQUIRED;
  return errors;
}

export function isAccountFormValid(errors: AccountFormErrors): boolean {
  return Object.keys(errors).length === 0;
}

export function toValidatedFormValues(
  values: AccountFormValues,
): ValidatedAccountFormValues | null {
  if (!values.bank) return null;
  return {
    bank: values.bank,
    agency: values.agency.trim(),
    account: values.account.trim(),
    active: values.active,
  };
}

export function buildCreateAccountBody(
  storeCode: number,
  values: ValidatedAccountFormValues,
): CreateAccountBody {
  return {
    store_code: storeCode,
    bank_number: values.bank.bankNumber,
    internal_code: values.bank.value,
    agency: values.agency,
    account: values.account,
    active: values.active,
  };
}

export function buildUpdateAccountBody(
  key: AccountKey,
  values: ValidatedAccountFormValues,
): UpdateAccountBody {
  return {
    store_code: key.store_code,
    bank_number: key.bank_number,
    agency: key.agency,
    sequence: key.sequence,
    account: values.account,
    active: values.active,
  };
}

export function requireAccountKey(account: Account | null): AccountKey {
  if (!account) {
    throw new Error(ACCOUNT_FORM_ERROR_MESSAGES.MISSING_ACCOUNT_FOR_EDIT);
  }
  return {
    store_code: account.store_code,
    bank_number: account.bank_number,
    agency: account.agency,
    sequence: account.sequence,
  };
}

export function requireStoreCode(storeCode: number | null): number {
  if (storeCode === null) {
    throw new Error(ACCOUNT_FORM_ERROR_MESSAGES.MISSING_STORE_FOR_CREATE);
  }
  return storeCode;
}
